import { MarkingPortalMapper } from '@src/models/mappers/MarkingPortalMapper';
import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import type {
  EmailPinSendResult,
  EmailPinSignInResult,
} from '@src/models/interfaces/emailPin.interface';
import type {
  MarkingPortalCheckInSessionParams,
  MarkingPortalCheckInSessionResult,
} from '@src/models/interfaces/markingPortal.interface';
import MarkingPortalRepository from '@src/repositories/MarkingPortalRepository';
import AttendanceRecordService from '@src/services/AttendanceRecordService';
import EmailPinService from '@src/services/EmailPinService';
import UserService from '@src/services/UserService';
import { DateUtil } from '@src/packages/utils/dates';
import safeExecute from '@src/util/safeExecute';

type MarkingPortalServiceDeps = {
  repository: MarkingPortalRepository;
  emailPinService: EmailPinService;
  attendanceRecordService: AttendanceRecordService;
  userService: UserService;
};

export default class MarkingPortalService {
  private readonly repository: MarkingPortalRepository;

  private readonly emailPinService: EmailPinService;

  private readonly attendanceRecordService: AttendanceRecordService;

  private readonly userService: UserService;

  constructor(
    repository: MarkingPortalRepository = new MarkingPortalRepository(),
    emailPinService: EmailPinService = new EmailPinService(),
    attendanceRecordService: AttendanceRecordService = new AttendanceRecordService(),
    userService: UserService = new UserService(),
  ) {
    this.repository = repository;
    this.emailPinService = emailPinService;
    this.attendanceRecordService = attendanceRecordService;
    this.userService = userService;
  }

  static fromDeps(deps: MarkingPortalServiceDeps): MarkingPortalService {
    return new MarkingPortalService(
      deps.repository,
      deps.emailPinService,
      deps.attendanceRecordService,
      deps.userService,
    );
  }

  sendTemporaryPin(email: string): Promise<EmailPinSendResult> {
    return this.emailPinService.sendTemporaryPin(email);
  }

  signInWithPin(email: string, code: string): Promise<EmailPinSignInResult> {
    return this.emailPinService.signInWithPin(email, code);
  }

  checkInSession(
    params: MarkingPortalCheckInSessionParams,
  ): Promise<MarkingPortalCheckInSessionResult> {
    return safeExecute(
      async () => {
        const portalToken = params.portalToken?.trim();

        if (!portalToken) {
          return { success: false, error: 'missing_portal_token' };
        }

        const lat = params.lat;
        const lng = params.lng;

        if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
          return { success: false, error: 'invalid_argument' };
        }

        if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
          return { success: false, error: 'invalid_argument' };
        }

        const checkInResult = await this.repository.checkIn({
          portalToken,
          lat,
          lng,
          accuracy: params.accuracy,
        });

        if (checkInResult.success === false) {
          return { success: false, error: checkInResult.error };
        }

        const { organizationId, attendanceRecordId } = checkInResult;

        if (!attendanceRecordId) {
          const orgUser = await this.userService.getUser(organizationId, params.userId);
          const timeZone = DateUtil.getDefaultTimeZone();

          return {
            success: true,
            organizationId,
            session: MarkingPortalMapper.toNoJourneySession(
              orgUser
                ? {
                    firstName: orgUser.firstName,
                    lastName: orgUser.lastName,
                    displayName: orgUser.displayName,
                    idCard: orgUser.idCard,
                    address: orgUser.address,
                  }
                : undefined,
              timeZone,
            ),
          };
        }

        const record = (await this.attendanceRecordService.getAttendanceRecord({
          organizationId,
          attendanceRecordId,
        })) as IAttendanceRecord | null;

        if (!record) {
          return { success: false, error: 'invalid_argument' };
        }

        const timeZone = DateUtil.getDefaultTimeZone();

        return {
          success: true,
          organizationId,
          attendanceRecordId,
          session: MarkingPortalMapper.toSessionFromAttendanceRecord(record, timeZone),
        };
      },
      'MarkingPortalService.checkInSession',
      { portalToken: params.portalToken },
    );
  }
}
